import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ChannelType = {
  EMAIL: 'EMAIL',
  WHATSAPP: 'WHATSAPP',
  CALL: 'CALL',
};

const determineApiBase = (searchParams) => {
//   const qp = searchParams.get('apiBase');
//   if (qp) return qp;
//   if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
//     return 'https://staging.prompthire.org';
//   }
  return "https://prompthire.org";
};

const parseBool = (val) => {
  if (typeof val === 'boolean') return val;
  if (!val) return undefined;
  const s = String(val).toLowerCase();
  if (['yes', 'true', '1', 'y'].includes(s)) return true;
  if (['no', 'false', '0', 'n'].includes(s)) return false;
  return undefined;
};

const Pill = ({ active, children, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-full border transition-colors duration-150 ${
      active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'
    }`}
  >
    {children}
  </button>
);

const Intent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [intentType, setIntentType] = useState(ChannelType.EMAIL);
  const [isInterested, setIsInterested] = useState(null);
  const [reply, setReply] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [applicantData, setApplicantData] = useState({
    applicant_id: '',
    jobopening_id: '',
    phone_no: '',
  });

  // Extract parameters from URL
  useEffect(() => {
    const id = searchParams.get('id');
    const intent = searchParams.get('intent');
    const jobId = searchParams.get('jobId');
    const phone = searchParams.get('phone');
    const type = searchParams.get('type') || 'EMAIL';
    
    // Create a complete data object with all URL parameters
    const updatedData = {
      applicant_id: id || '',
      jobopening_id: jobId || '',
      phone_no: phone || ''
    };
    
    // Update applicant data state with all parameters at once
    setApplicantData(updatedData);
    
    // Set intent type if provided
    if (Object.values(ChannelType).includes(type.toUpperCase())) {
      setIntentType(type.toUpperCase());
    }
    
    // Auto-submit if intent is provided in URL
    if (intent) {
      const isInterestedValue = parseBool(intent) ?? (intent.toLowerCase() === 'yes');
      setIsInterested(isInterestedValue);
      
      // If it's from email, auto-submit after a delay to ensure state is updated
      if (type.toUpperCase() === ChannelType.EMAIL) {
        // Use a longer timeout to ensure state updates are processed
        setTimeout(() => {
          // Pass the complete data directly to handleSubmit to avoid state timing issues
          handleSubmitWithData(isInterestedValue, updatedData, type.toUpperCase());
        }, 1000);
      }
    }
  }, [searchParams]);
  
  // New function that takes data directly instead of relying on state
  const handleSubmitWithData = async (interested, data, channelType) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const apiBase = determineApiBase(searchParams);
      
      const payload = {
        applicant_id: data.applicant_id,
        jobopening_id: data.jobopening_id,
        phone_no: data.phone_no,
        is_interested: interested,
        intent: {
          type: channelType || intentType,
          intent: interested,
          reply: reply
        }
      };
      
      // Add WaReply if it's WhatsApp
      if (channelType === ChannelType.WHATSAPP) {
        payload.WaReply = reply;
      }
      
      console.log('Submitting with payload:', payload);
      
      const response = await axios.patch(
        `${apiBase}/api/applicant/update-interested-intent`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.status === 200) {
        setSuccess(true);
        setSubmitted(true);
      } else {
        throw new Error('Failed to update intent');
      }
    } catch (err) {
      console.error('Error updating intent:', err);
      setError(err.message || 'Failed to update intent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (interested = isInterested) => {
    if (loading) return;
    
    if (interested === null) {
      setError('Please select whether you are interested or not.');
      return;
    }
    
    // Use the current state values when manually submitting
    handleSubmitWithData(interested, applicantData, intentType);
  };

  // Success screen component
  const SuccessScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-[80vh] py-10 px-4 bg-gradient-to-b from-gray-50 to-white">
      {isInterested ? (
        // Interested Response - Modern UI
        <div className="w-full max-w-lg mx-auto">
          <div className="mb-8 flex justify-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"></path>
              </svg>
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-center mb-2 text-gray-800">Thanks for your interest!</h2>
          <p className="text-center text-gray-600 mb-8">We're excited to connect with you</p>
          
          <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-blue-100">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 py-4 px-6">
              <h3 className="text-white text-lg font-medium">Next Steps</h3>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-gray-800">Hey! 👋 Thanks for showing interest. We'll reach out to you shortly with the next steps.</p>
                </div>
              </div>
              

              
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-gray-800 mb-2">If you have any questions or need support, feel free to call us:</p>
                  <a href="tel:+916201329712" className="inline-flex items-center text-blue-600 font-medium hover:text-blue-800 transition-colors">
                    <span className="text-lg">📞 +91 6201329712</span>
                  </a>
                </div>
              </div>
              
              
            </div>
          </div>
          
          
        </div>
      ) : (
        // Not Interested Response - Modern UI
        <div className="w-full max-w-lg mx-auto">
          <div className="mb-8 flex justify-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2"></path>
              </svg>
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-center mb-2 text-gray-800">Thanks for your response</h2>
          <p className="text-center text-gray-600 mb-8">We appreciate you taking the time</p>
          <p className="text-center text-gray-500 mb-8">You can close this window now.</p>
          
          <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-blue-100">
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 py-4 px-6">
              <h3 className="text-white text-lg font-medium">We understand</h3>
            </div>
            
            <div className="p-6 space-y-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-gray-800">No worries! 😊 We understand timing matters.</p>
                </div>
              </div>
              
              
              
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-gray-800 mb-0">For any questions, you can always call us:</p>
                  <a href="tel:+916201329712" className="inline-flex items-center text-blue-600 font-medium hover:text-blue-800 transition-colors">
                    <span className="text-lg">📞 +916201329712</span>
                  </a>
                </div>
              </div>
              
              
            </div>
          </div>
          
          
        </div>
      )}
    </div>
  );

  // Error screen component
  const ErrorScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
      <div className="w-16 h-16 mb-6 rounded-full bg-red-100 flex items-center justify-center">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </div>
      <h2 className="text-2xl font-bold mb-2">Something Went Wrong</h2>
      <p className="text-gray-600 mb-6">
        {error || "We couldn't process your response. Please try again."}
      </p>
      <button 
        onClick={() => setError(null)}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Try Again
      </button>
    </div>
  );

  // Main form component
  const IntentForm = () => (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-center">Job Opportunity Response</h2>
      
      {/* Intent Type Pills - Only show if not auto-submitted */}
      {!submitted && intentType !== ChannelType.EMAIL && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Response Channel</label>
          <div className="flex flex-wrap gap-2">
            {Object.values(ChannelType).map((type) => (
              <Pill 
                key={type} 
                active={intentType === type}
                onClick={() => setIntentType(type)}
              >
                {type.charAt(0) + type.slice(1).toLowerCase()}
              </Pill>
            ))}
          </div>
        </div>
      )}
      
      {/* Interest Selection */}
      {!submitted && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Are you interested in this job opportunity?
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setIsInterested(true)}
              className={`p-4 rounded-lg border-2 transition-all ${
                isInterested === true
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"></path>
                </svg>
              </div>
              <div className="font-medium">Yes, I'm Interested</div>
            </button>
            
            <button
              onClick={() => setIsInterested(false)}
              className={`p-4 rounded-lg border-2 transition-all ${
                isInterested === false
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2"></path>
                </svg>
              </div>
              <div className="font-medium">No, Not Interested</div>
            </button>
          </div>
        </div>
      )}
      
      {/* Additional Reply - Only for WhatsApp or if explicitly needed */}
      {!submitted && isInterested !== null && (intentType === ChannelType.WHATSAPP || intentType === ChannelType.CALL) && (
        <div className="mb-6">
          <label htmlFor="reply" className="block text-sm font-medium text-gray-700 mb-2">
            Additional Comments (Optional)
          </label>
          <textarea
            id="reply"
            rows="3"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Add any additional comments here..."
          ></textarea>
        </div>
      )}
      
      {/* Submit Button */}
      {!submitted && (
        <div className="flex justify-center">
          <button
            onClick={() => handleSubmit()}
            disabled={loading || isInterested === null}
            className={`w-full py-2 px-4 rounded-md text-white font-medium transition-colors ${
              loading || isInterested === null
                ? 'bg-gray-400 cursor-not-allowed'
                : isInterested
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              'Submit Response'
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {error ? (
          <ErrorScreen />
        ) : success ? (
          <SuccessScreen />
        ) : (
          <IntentForm />
        )}
      </div>
    </div>
  );
};

export default Intent;
